import React, { useState, useEffect } from 'react';
import Select, { GroupBase, OptionsOrGroups, StylesConfig } from "react-select";
import { IoAddCircle } from 'react-icons/io5';
import { SelectErrorTheme, SelectSuccessTheme } from '../constants/SelectTheme';

interface SelectInputBoxProps {
    name: string;
    required?: boolean;
    clear?: boolean;
    disabled?: boolean;
    add_btn?: boolean;
    onClick?: () => void;
    label?: string;
    placeholder?: string;
    value?: any;
    isLoading?: boolean;
    optionSelect: (name: string, selectedOption: any) => void;
    handleBlur?: (event: React.FocusEvent<HTMLElement>) => void;
    touched?: boolean;
    errors?: string;
    login?: boolean;
    options?: OptionsOrGroups<any, GroupBase<any>>;
    loadOptions?: (inputValue: string) => Promise<any[]>; // Custom loadOptions function
    debouncedHandleInputChange?: (inputValue: string) => void; // Updated to pass the inputValue
}

const SelectInputBox: React.FC<SelectInputBoxProps> = ({
    name, required, clear, disabled, add_btn, onClick,
    label, placeholder, value, optionSelect, handleBlur,
    touched, errors, options, isLoading, debouncedHandleInputChange
}) => {
    const storedTheme = localStorage.getItem('theme');
    const colorTheme = storedTheme || 'light'; 
    const [selectedOption, setSelectedOption] = useState<any>(null);
        useEffect(() => {
        // If value was cleared, clear the display too
        if (value === undefined || value === null || value === "") {
            setSelectedOption(null);
            return;
        }
        // Only update if we can find a real match — never clear
        // just because the current (search-filtered) options list
        // doesn't happen to include the previously selected item.
        if (options) {
            const match = options.find(option => option.value === value);
            if (match) {
                setSelectedOption(match);
            }
        }
    }, [value, options, colorTheme]);

    const customStyles: StylesConfig<any, false> = {
        control: (provided, state) => ({
            ...provided,
            background: colorTheme === 'dark' ? "transparent" : '#fff',
            borderColor: errors && touched ? "red" : colorTheme === 'dark' ? "#0c4a6e" : "#9ca3af",
            height: "30px",
            borderRadius: '3px',
            outline: "none",
            borderWidth: "1px",
            boxShadow: state.isFocused ? '0 0 0 1px green' : 'none', // Green border on focus
            fontFamily: 'Public Sans, sans-serif',
            minHeight: "30px",
            bottom: "2px",
            color: colorTheme === 'dark' ? 'white' : '#1e293b',
        }),
        valueContainer: (provided) => ({
            ...provided,
            height: "30px",
            bottom: "2px",
            background: 'transparent'

        }),
        input: (provided) => ({
            ...provided,
            fontSize: "12px",
            color: colorTheme === 'dark' ? 'white' : '#1e293b',
            fontWeight: 200,
            fontFamily: 'Public Sans, sans-serif',
            bottom: "2px",
        }),
        indicatorSeparator: () => ({
            display: 'none',
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: '30px',
            bottom: "2px",
        }),
        placeholder: (provided) => ({
            ...provided,
            marginTop: "0px",
            fontWeight: 400,
            fontSize: '12px',
            fontFamily: 'Public Sans, sans-serif',
            color: colorTheme === 'dark' ? "#6b7280" : "#6a7282",
            opacity: "1",
            userSelect: "none",
            overflow: "hidden",
            textWrap:"nowrap"
        }),
        option: (provided) => ({
            ...provided,
            zIndex: 9999999999999,
            position: "sticky",
            paddingLeft: '10px',
            paddingRight: '5px',
            paddingTop: '5px',
            paddingBottom: '5px',
            fontSize: '12px',
            fontFamily: 'Public Sans, sans-serif',

        }),
        singleValue: (provided) => ({
            ...provided,
            color: colorTheme === 'dark' ? 'white' : '#1e293b', // Set the color for the selected value
            fontFamily: 'Public Sans, sans-serif',
            fontSize: '12px',
        }),
    };

    return (
        <div>
            {label && (
                <label htmlFor='' className='text-[12px] mb-0.5 font-medium text-gray-700 dark:text-gray-200 flex gap-0.5 font-public-sans'>
                    {label} {required && <span className='text-red-600'>*</span>}
                    {add_btn && <IoAddCircle onClick={onClick} className='text-sky-600 text-[15px] hover:text-gray-400 cursor-pointer' />}
                </label>
            )}
            <Select
                options={options}
                openMenuOnFocus
                isClearable={clear}
                onChange={(selectedOption) => optionSelect(name, selectedOption)}
                theme={errors && touched ? SelectErrorTheme : SelectSuccessTheme}
                value={selectedOption || ""}
                onInputChange={(inputValue, actionMeta) => {
                    // Only fire the API search on genuine user typing —
                    // ignore set-value / menu-close / input-blur / etc.
                    if (actionMeta.action === 'input-change') {
                        debouncedHandleInputChange && debouncedHandleInputChange(inputValue);
                    }
                }} // Trigger API call on input change
                isSearchable
                isDisabled={disabled || false}
                styles={customStyles}
                className="text-[12px] mt-0"
                onBlur={handleBlur}
                placeholder={placeholder || ""}
                required={required}
                isLoading={isLoading}
            />
            {errors && touched && <p className="text-red-500 text-[10px]">{errors}</p>}


        </div>
    );
};

export default SelectInputBox;
